import { useState } from 'react'
import { supabase } from './supabaseClient'
import { matchScore, fmtDate } from './utils/nameNorm'
import { buildPersonSearchQuery, parsePersonName } from './utils/search'
import { fetchStoneCoords } from './utils/mapData'
import { useVisitorTracking } from './hooks/useVisitorTracking'
import { haversineMeters, distanceLabel, openInMaps } from './utils/geo'
import StoneMap from './components/StoneMap'

const PATRIOT_AFFILIATIONS = {
  association_papers: { label: 'Signed Association Papers', color: '#6ee7b7' },
  ny_militia:         { label: 'NY Militia',                color: '#6ee7b7' },
  continental_army:   { label: 'Continental Army',          color: '#6ee7b7' },
  culper_ring:        { label: 'Culper Ring',               color: '#93c5fd' },
  loyalist:           { label: 'Crown Loyalist',            color: '#fca5a5' },
}

export default function Search({ onLogin, onHome }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [selected, setSelected] = useState(null)
  const [stoneData, setStoneData] = useState(null)
  const [searching, setSearching] = useState(false)
  const { visitorLocation, isTracking, startTracking, stopTracking } = useVisitorTracking()

  const handleSearch = async () => {
    if (!query.trim()) return
    setSearching(true)
    setResults(null)
    setSelected(null)
    setStoneData(null)

    const { lastName, firstTerms } = parsePersonName(query)
    const { data, error } = await buildPersonSearchQuery(query, null, 50)

    if (error) {
      console.error(error)
      alert('Search error: ' + error.message)
    } else {
      const queryPerson = !lastName
        ? { last_name: firstTerms[0] }
        : { first_name: firstTerms.join(' '), last_name: lastName }
      const scored = (data || [])
        .map(r => ({ record: r, score: matchScore(queryPerson, r) }))
        .sort((a, b) => b.score - a.score)
      setResults(scored.map(s => s.record))
    }
    setSearching(false)
  }

  const selectRecord = async (record) => {
    setSelected(record)
    setStoneData(null)

    if (record.is_occupant) {
      const { data, error } = await supabase
        .from('stone_deceased')
        .select('stones ( stone_id, gps_accuracy_m, condition_notes, inscription_text, location, stone_photos ( photo_url, is_primary ) )')
        .eq('deceased_id', record.deceased_id)
        .eq('role', 'occupant')

      if (!error && data && data.length > 0) {
        // Prefer the stone that has photos (imported stone + photographed stone may both exist)
        const best = data.find(function(d) { return d.stones?.stone_photos?.length > 0 }) || data[0]
        if (best?.stones) {
          const stoneCoord = await fetchStoneCoords(best.stones.stone_id)
          setStoneData(Object.assign({}, best.stones, {
            lat: stoneCoord ? stoneCoord.lat : null,
            lng: stoneCoord ? stoneCoord.lng : null
          }))
        }
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="bg-gray-800 p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-green-400">Granite Graph</h1>
          <p className="text-gray-400 text-xs">Historic Cemetery Records</p>
        </div>
        <div className="flex gap-3">
          {onHome && (
            <button onClick={onHome} className="text-gray-300 text-sm hover:text-white">
              Photograph
            </button>
          )}
          {onLogin && (
            <button onClick={onLogin} className="text-gray-300 text-sm hover:text-white border border-gray-600 px-3 py-1 rounded">
              Volunteer Login
            </button>
          )}
        </div>
      </div>

      <div className="p-4 max-w-lg mx-auto">
        <div className="mb-6">
          <p className="text-gray-300 text-sm mb-3">Search by last name, first name, or first and last name.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={function(e) { setQuery(e.target.value) }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleSearch() }}
              placeholder="Last name, First name, or First Last"
              className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="bg-green-700 hover:bg-green-600 text-white font-bold px-4 rounded-lg"
            >
              {searching ? '...' : 'Search'}
            </button>
          </div>
        </div>

        {results && !selected && (
          <div>
            <p className="text-green-400 font-bold mb-3">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
            {results.length === 0 && (
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-gray-300">No records found. Try a different spelling.</p>
              </div>
            )}
            {results.map(function(record) {
              return (
                <div
                  key={record.deceased_id}
                  onClick={function() { selectRecord(record) }}
                  className="bg-gray-800 rounded-lg p-4 mb-2 cursor-pointer hover:bg-gray-700 border border-gray-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-white">{record.full_name}</p>
                      {record.maiden_name && <p className="text-gray-300 text-sm">nee {record.maiden_name}</p>}
                      <div className="flex gap-3 mt-1">
                        {record.date_of_birth_verbatim && <p className="text-gray-300 text-xs">b. {fmtDate(record.date_of_birth_verbatim, record.date_of_birth_parsed)}</p>}
                        {record.date_of_death_verbatim && <p className="text-gray-300 text-xs">d. {fmtDate(record.date_of_death_verbatim, record.date_of_death_parsed)}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      {record.is_photographed
                        ? <span className="text-green-400 text-xs">📷 Photographed</span>
                        : record.is_occupant
                        ? <span className="text-blue-400 text-xs">📋 Cataloged</span>
                        : record.is_mentioned
                        ? <span className="text-yellow-400 text-xs">📝 Referenced on stone</span>
                        : <span className="text-gray-400 text-xs">Not yet cataloged</span>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {selected && (
          <div>
            <button
              onClick={function() { stopTracking(); setSelected(null); setStoneData(null) }}
              className="text-gray-300 text-sm hover:text-white mb-4 flex items-center gap-1"
            >
              Back to results
            </button>

            {stoneData && stoneData.stone_photos && stoneData.stone_photos.length > 0 && (
              <img
                src={(stoneData.stone_photos.find(function(p) { return p.is_primary }) || stoneData.stone_photos[0]).photo_url}
                alt="Gravestone"
                className="w-full rounded-lg mb-4"
              />
            )}
            {stoneData && (!stoneData.stone_photos || stoneData.stone_photos.length === 0) && (
              <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-sm">Stone cataloged from historical records — field photo not yet on file.</p>
              </div>
            )}

            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <h2 className="text-xl font-bold text-white mb-1">{selected.full_name}</h2>
              {selected.maiden_name && <p className="text-gray-300 text-sm mb-2">nee {selected.maiden_name}</p>}
              <div className="flex gap-4">
                {selected.date_of_birth_verbatim && (
                  <div>
                    <p className="text-gray-400 text-xs">Born</p>
                    <p className="text-white text-sm">{fmtDate(selected.date_of_birth_verbatim, selected.date_of_birth_parsed)}</p>
                  </div>
                )}
                {selected.date_of_death_verbatim && (
                  <div>
                    <p className="text-gray-400 text-xs">Died</p>
                    <p className="text-white text-sm">{fmtDate(selected.date_of_death_verbatim, selected.date_of_death_parsed)}</p>
                  </div>
                )}
              </div>
              {selected.biography && <p className="text-gray-300 text-sm mt-3">{selected.biography}</p>}
              {selected.patriot_affiliations && selected.patriot_affiliations.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.patriot_affiliations.map(function(v) {
                    const a = PATRIOT_AFFILIATIONS[v]
                    if (!a) return null
                    return (
                      <span key={v} style={{ color: a.color, border: `1px solid ${a.color}`, borderRadius: 99, fontSize: 12, padding: '2px 10px' }}>
                        {a.label}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>

            {stoneData && (
  <div className="bg-gray-800 rounded-lg p-4 mb-4">
    <p className="text-gray-400 text-xs font-bold uppercase tracking-wide mb-3">Stone Record</p>
    
    {stoneData.inscription_text && (
      <div className="mb-3">
        <p className="text-gray-400 text-xs mb-1">Inscription</p>
        <p className="text-white text-sm font-mono leading-relaxed">
          {stoneData.inscription_text.split('|').map(s => s.trim()).filter(Boolean).join('\n')}
        </p>
      </div>
    )}

    {stoneData.stone_condition && stoneData.stone_condition !== 'good' && (
      <div className="mb-2">
        <p className="text-gray-400 text-xs mb-1">Condition</p>
        <p className={
          stoneData.stone_condition === 'poor' || stoneData.stone_condition === 'illegible'
            ? 'text-red-400 text-sm capitalize'
            : 'text-yellow-400 text-sm capitalize'
        }>
          {stoneData.stone_condition}
        </p>
      </div>
    )}

    {stoneData.gps_accuracy_m && (
      <p className="text-gray-500 text-xs mt-2">
        📍 GPS accuracy: {Number(stoneData.gps_accuracy_m).toFixed(1)}m
      </p>
    )}
  </div>
)}

            {stoneData && stoneData.lat && stoneData.lng && (
              <div className="mb-4">
                <div className="flex items-baseline gap-3 mb-2">
                  <p className="text-green-400 font-bold">Find this stone</p>
                  {visitorLocation && (
                    <span className="text-white text-sm">
                      {distanceLabel(haversineMeters(visitorLocation.lat, visitorLocation.lng, stoneData.lat, stoneData.lng))}
                    </span>
                  )}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <StoneMap
                    stones={[{ stone_id: stoneData.stone_id, lat: stoneData.lat, lng: stoneData.lng }]}
                    visitorLocation={visitorLocation}
                    showRoute
                    height="220px"
                    zoom={19}
                    renderPopup={() => selected.full_name}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={function() { openInMaps(stoneData.lat, stoneData.lng) }}
                    className="flex-1 bg-blue-700 hover:bg-blue-600 text-white py-3 rounded-lg font-bold text-sm"
                  >
                    Open in Maps
                  </button>
                  {!isTracking ? (
                    <button
                      onClick={startTracking}
                      className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-bold text-sm"
                    >
                      Track My Location
                    </button>
                  ) : (
                    <button
                      onClick={stopTracking}
                      className="flex-1 bg-green-900 hover:bg-green-800 text-white py-3 rounded-lg font-bold text-sm"
                    >
                      {visitorLocation ? '● Tracking' : 'Locating...'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {!selected.is_occupant && (
              <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                <p className="text-gray-300 text-sm">This stone has not been photographed yet.</p>
                {!onLogin && (
                  <button
                    onClick={onHome}
                    className="mt-3 w-full bg-green-700 hover:bg-green-600 text-white font-bold py-2 rounded-lg text-sm"
                  >
                    Photograph this stone
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}